import type { DragEndEvent } from "@dnd-kit/core";
import { closestCenter, DndContext, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Plus, Trash2 } from "lucide-react";
import { useId } from "react";
import { cn } from "../../lib/utils";
import { Button } from "./Button";
import { Input } from "./Input";

type StringArrayEditorProps = {
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
};

export function StringArrayEditor({
  value,
  onChange,
  placeholder = "Enter value...",
}: StringArrayEditorProps) {
  const idPrefix = useId();
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const items = value.map((v, i) => ({ id: `${idPrefix}-${i}`, value: v }));

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = items.findIndex((item) => item.id === active.id);
    const newIndex = items.findIndex((item) => item.id === over.id);
    const next = [...value];
    const [removed] = next.splice(oldIndex, 1);
    next.splice(newIndex, 0, removed);
    onChange(next);
  };

  const handleAdd = () => {
    onChange([...value, ""]);
  };

  const handleRemove = (index: number) => {
    onChange(value.filter((_, i) => i !== index));
  };

  const handleChange = (index: number, newValue: string) => {
    const next = [...value];
    next[index] = newValue;
    onChange(next);
  };

  if (value.length === 0) {
    return (
      <div className="text-center py-4">
        <p className="text-xs text-surface-400 mb-2">No items</p>
        <Button variant="outline" size="sm" onClick={handleAdd}>
          <Plus className="h-3.5 w-3.5 mr-1.5" />
          Add Item
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext
          items={items.map((item) => item.id)}
          strategy={verticalListSortingStrategy}
        >
          {items.map((item, index) => (
            <SortableStringItem
              key={item.id}
              id={item.id}
              value={item.value}
              placeholder={placeholder}
              onChange={(v) => handleChange(index, v)}
              onRemove={() => handleRemove(index)}
            />
          ))}
        </SortableContext>
      </DndContext>
      <Button variant="ghost" size="sm" onClick={handleAdd} className="w-full">
        <Plus className="h-3.5 w-3.5 mr-1.5" />
        Add Item
      </Button>
    </div>
  );
}

type SortableStringItemProps = {
  id: string;
  value: string;
  placeholder: string;
  onChange: (value: string) => void;
  onRemove: () => void;
};

function SortableStringItem({
  id,
  value,
  placeholder,
  onChange,
  onRemove,
}: SortableStringItemProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn("flex items-center gap-1.5 group", isDragging && "opacity-50")}
    >
      <button
        type="button"
        className="p-1 cursor-grab active:cursor-grabbing text-surface-500 hover:text-surface-300"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-3.5 w-3.5" />
      </button>
      <Input
        size="xs"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        wrapperClassName="flex-1"
      />
      <button
        type="button"
        onClick={onRemove}
        className="p-1 text-surface-500 hover:text-error-400 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity"
      >
        <Trash2 className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
